import React from "react";
import classNames from "classnames";
import type { PickerFullscreenRenderProps } from "@omega/ui";
import { CardHeader } from "@shared/ui/card-header";
import { ViewportLayer } from "@shared/ui/overlay/floating-layer";
import { overlayTitleText } from "@shared/ui/overlay/overlayTitleText";
import "@shared/ui/overlay/mobile-window-frame/MobileWindowFrame.scss";
import "@shared/ui/overlay/picker-overlay/PickerOverlay.scss";

/** Опции композиции поверх базового OMP fullscreen shell. */
export type RenderOmpPickerFullscreenOptions = {
    /** Нижняя область shell (например close-кнопка Select/SearchSelect). */
    footer?: React.ReactNode;
};

/**
 * Fullscreen shell для `@omega/ui` picker: визуально как {@link MobileWindowFrame},
 * без зависимости от overlay window stack (`useWindowControls`).
 *
 * Базовый chrome: header + content. Footer — opt-in через `options.footer`
 * (не для всех пикеров, а для field-фасадов вроде Select/SearchSelect).
 *
 * Используется через `UiProvider.renderPickerFullscreen`.
 * @param props контракт fullscreen shell от `@omega/ui`
 * @param options дополнительная композиция (footer и т.п.)
 */
export function renderOmpPickerFullscreen(
    { Dialog, children, dialogProps, dismiss, label, open }: PickerFullscreenRenderProps,
    options?: RenderOmpPickerFullscreenOptions,
): React.ReactNode {
    const dialogClassName = classNames(
        "omp-omega-picker-fullscreen",
        "shared_picker_overlay__fullscreen",
        "shared_picker_overlay__fullscreen--window",
        "mobile_window_frame",
        "mobile_window_frame__fullscreen",
        "spp-ui-theme",
        "omp-omega-ui",
    );

    const footer = options?.footer;

    return (
        <ViewportLayer
            open={open}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    dismiss("dismiss");
                }
            }}
            className="shared_picker_overlay__fullscreen_layer"
        >
            {Dialog({
                "aria-label": dialogProps["aria-label"],
                id: dialogProps.id,
                className: dialogClassName,
                children: (
                    <>
                        <div className={classNames("omp-omega-picker-fullscreen__header", "mobile_window_frame__header")}>
                            <CardHeader
                                density="compact"
                                text={overlayTitleText(label)}
                                onClose={() => dismiss("dismiss")}
                            />
                        </div>
                        <div
                            className={classNames(
                                "omp-omega-picker-fullscreen__content",
                                "shared_picker_overlay__fullscreen_panel",
                                "mobile_window_frame__content",
                                "custom_scrollbar",
                            )}
                        >
                            {children}
                        </div>
                        {footer != null ? (
                            <div
                                className={classNames("omp-omega-picker-fullscreen__footer", "mobile_window_frame__footer")}
                            >
                                {footer}
                            </div>
                        ) : null}
                    </>
                ),
            })}
        </ViewportLayer>
    );
}
